/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { useAuth0 } from '@auth0/auth0-react'
import { t } from '@lingui/macro'
import PhoneIcon from '@mui/icons-material/Phone'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import {
    CandidateSearchDropdown,
    CarePartnersService,
    CarePartnerUpdateDTO,
    useCandidates,
    useCarePartner,
    usePatients,
    useUpdateCandidateCarePartner
} from '@om1/aspen-api'
import { AspenCandidatesService, CandidateDTO, CandidatePatchDTO } from '@om1/aspen-api/codegen/client'
import { useContactAttempts } from '@om1/aspen-api/codegen/hooks/useContactAttempts'
import { usePatientManagers } from '@om1/aspen-api/codegen/hooks/usePatientManagers'
import { formatDate } from '@om1/platform-components'
import { AlertTriangle, Calendar, Edit, HeartHandshake, Phone, Plus, Users, X } from 'lucide-react'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { AdditionalPhoneData, AdditionalPhoneModal } from '../components/AdditionalPhoneModal'
import EditContactInfoModal from '../components/EditContactInfoModal'
import LoadingComponent from '../components/LoadingComponent'
import { LogAttemptInput } from '../components/LogAttemptInput'
import { LogAttemptTable } from '../components/LogAttemptTable'
import { StatusBadge } from '../components/StatusBadge'
import { SubjectHistory } from '../components/SubjectHistory'
import GenericDetailPage from './GenericDetailPage'

// Component to display care partner information in a clean, Apple-style layout
const CarePartnerInfoSection = ({
    title,
    icon,
    children,
    className = '',
    titleAction
}: {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    className?: string
    titleAction?: React.ReactNode
}) => {
    return (
        <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm ${className}`}>
            <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='flex items-center gap-2'>
                    <div className='text-primary-main'>{icon}</div>
                    <h3 className='text-base font-semibold text-gray-800 m-0'>{title}</h3>
                </div>
                {titleAction}
            </div>
            <div className='p-4'>{children}</div>
        </div>
    )
}

// Info field component for consistent styling
const InfoField = ({ label, value, status }: { label: string; value: React.ReactNode; status?: string | null }) => {
    const isPreferredContactTime = label === t`Preferred Contact Time`
    const showWarning = isPreferredContactTime && isDateInPast(value as string) && status === 'candidate'

    return (
        <div className='mb-3 last:mb-0'>
            <p className='text-xs text-gray-500 mb-0.5'>{label}</p>
            <div className='flex items-center gap-2'>
                <p className='text-sm font-medium break-words'>{value || 'N/A'}</p>
                {showWarning && <AlertTriangle size={16} className='text-orange-500' />}
            </div>
        </div>
    )
}

// Format time slot for display in EST
const formatTimeSlot = (startDatetime?: string | null, endDatetime?: string | null) => {
    if (!startDatetime || !endDatetime) return 'N/A'

    const start = new Date(startDatetime)
    const end = new Date(endDatetime)

    // Convert UTC to EST for display
    const startTime = start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    })
    const endTime = end.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    })

    // Map the times to proper time slot labels
    // Get hours in EST timezone
    const startHourEST = start.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false
    })
    const endHourEST = end.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false
    })

    // Format the date in European format (DD/MM/YY) using the common utility
    // Get date components in EST timezone
    const estYear = start.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' })
    const estMonth = start.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit' })
    const estDay = start.toLocaleString('en-US', { timeZone: 'America/New_York', day: '2-digit' })
    // Create a date string in YYYY-MM-DD format and format it using the common utility
    const estDateString = `${estYear}-${estMonth}-${estDay}`
    const dateInEST = formatDate(estDateString)

    // Determine the time slot based on the hours in EST
    if (startHourEST === '9' && endHourEST === '13') {
        return `${dateInEST}, 9:00 AM – 1:00 PM EST`
    } else if (startHourEST === '13' && endHourEST === '17') {
        return `${dateInEST}, 1:00 PM – 5:00 PM EST`
    } else {
        // Fallback to exact times if they don't match standard slots
        return `${dateInEST}, ${startTime} – ${endTime} EST`
    }
}

// Add this helper function near other utility functions
const isDateInPast = (dateString?: string | null) => {
    if (!dateString) return false
    return new Date(dateString) < new Date()
}

const CarePartnerDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const history = useHistory()
    const { carePartner, fetchCarePartner } = useCarePartner(id || '')

    // Fetch assigned candidates using the care_partner_id filter
    const candidatesFilters = useMemo(
        () => ({
            carePartnerId: id || ''
        }),
        [id]
    )
    const {
        response: candidatesResponse,
        loading: candidatesLoading,
        refetch: refetchCandidates
    } = useCandidates(1, 1000, 'first_name:asc', candidatesFilters)

    // Fetch assigned patients using the care_partner_id filter
    const patientsFilters = useMemo(
        () => ({
            carePartnerId: id || ''
        }),
        [id]
    )
    const { response: patientsResponse, loading: patientsLoading } = usePatients(1, 1000, 'first_name:asc', patientsFilters)

    // Fetch patient managers (including inactive for historical records) to pass to SubjectHistory
    // Memoize params to prevent infinite re-renders (usePatientManagers depends on params object reference)
    const patientManagersParams = useMemo(
        () => ({
            page: 1,
            limit: 1000, // Get all patient managers
            active: null, // Include inactive for historical records
            sort: 'name:asc'
        }),
        []
    )
    const { patientManagers } = usePatientManagers(patientManagersParams)

    const {
        contactAttempts,
        isLoading: contactAttemptsLoading,
        error: contactAttemptsError,
        fetchContactAttempts,
        submitAttempt,
        updateAttempt
    } = useContactAttempts({
        entityType: 'care-partner',
        entityId: id || ''
    })

    const [editingAttempt, setEditingAttempt] = useState<any>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false)
    const [isUpdatingContact, setIsUpdatingContact] = useState(false)
    const [isAdditionalPhoneModalOpen, setIsAdditionalPhoneModalOpen] = useState(false)
    const [isUpdatingAdditionalPhones, setIsUpdatingAdditionalPhones] = useState(false)
    const [editingPhoneSlot, setEditingPhoneSlot] = useState<'phone1' | 'phone2' | undefined>(undefined)
    const [isRemovingCandidate, setIsRemovingCandidate] = useState<string | null>(null)
    const [isAddCandidateModalOpen, setIsAddCandidateModalOpen] = useState(false)
    const [selectedCandidate, setSelectedCandidate] = useState<CandidateDTO | null>(null)
    const [isAssigningCandidate, setIsAssigningCandidate] = useState(false)
    const { user } = useAuth0()
    const { updateCandidateCarePartner } = useUpdateCandidateCarePartner()

    useEffect(() => {
        if (id) {
            fetchContactAttempts()
        }
    }, [id, fetchContactAttempts])

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingAttempt(null)
    }

    const handleSubmitAttempt = async (data: any) => {
        await submitAttempt(data)
        handleCloseModal()
    }

    const handleEditAttempt = (attempt: any) => {
        setEditingAttempt(attempt)
        setIsModalOpen(true)
    }

    const handleUpdateAttempt = async (data: any) => {
        if (!editingAttempt) return
        const success = await updateAttempt({
            contactAttemptId: editingAttempt.id,
            contactType: data.contactType,
            outcome: data.outcome,
            notes: data.notes,
            nextContactTime: data.nextContactTime
        })
        if (success) {
            setEditingAttempt(null)
            handleCloseModal()
        }
    }

    const handleUpdateContactInfo = async (patchData: CarePartnerUpdateDTO | CandidatePatchDTO) => {
        setIsUpdatingContact(true)
        try {
            await CarePartnersService.updateCarePartnerCarePartnersCarePartnerIdPatch({
                carePartnerId: id!,
                requestBody: patchData as CarePartnerUpdateDTO
            })
            // Refresh care partner data
            await fetchCarePartner()
            setIsEditContactModalOpen(false)
        } catch (error) {
            console.error('Error updating contact information:', error)
            throw error // Re-throw to let the modal handle the error display
        } finally {
            setIsUpdatingContact(false)
        }
    }

    const handleUpdateAdditionalPhones = async (data: AdditionalPhoneData) => {
        if (!carePartner) return

        setIsUpdatingAdditionalPhones(true)
        try {
            const patchData: CarePartnerUpdateDTO = {}

            // Update the appropriate slot
            if (data.slot === 'phone1') {
                patchData.additionalPhone1 = data.phone
                patchData.additionalPhone1Label = data.label
            } else {
                patchData.additionalPhone2 = data.phone
                patchData.additionalPhone2Label = data.label
            }

            await CarePartnersService.updateCarePartnerCarePartnersCarePartnerIdPatch({
                carePartnerId: id!,
                requestBody: patchData
            })
            await fetchCarePartner()
            setIsAdditionalPhoneModalOpen(false)
        } catch (error) {
            console.error('Error updating additional phone numbers:', error)
            throw error
        } finally {
            setIsUpdatingAdditionalPhones(false)
        }
    }

    const handleRemoveCandidate = async (candidateId: string, e: React.MouseEvent) => {
        e.stopPropagation() // Prevent navigation when clicking remove button
        if (!window.confirm(t`Are you sure you want to remove this candidate from this care partner?`)) {
            return
        }

        setIsRemovingCandidate(candidateId)
        try {
            // Set care_partner_id to null to remove the assignment
            // Note: Once the DTO is regenerated, carePartnerId will be available
            await AspenCandidatesService.patchCandidatesCandidateIdPatch({
                candidateId,
                requestBody: {
                    id: candidateId,
                    carePartnerId: null
                } as any // Temporary - will be properly typed after DTO regeneration
            })
            // Refetch candidates list
            await refetchCandidates()
        } catch (error) {
            console.error('Error removing candidate assignment:', error)
            alert(t`Failed to remove candidate assignment. Please try again.`)
        } finally {
            setIsRemovingCandidate(null)
        }
    }

    const handleAddCandidateClick = () => {
        setIsAddCandidateModalOpen(true)
        setSelectedCandidate(null)
    }

    const handleAssignCandidate = async () => {
        if (!selectedCandidate || !carePartner || !user) return

        setIsAssigningCandidate(true)
        try {
            await updateCandidateCarePartner(selectedCandidate.id, id!, user.org_id || '')
            setIsAddCandidateModalOpen(false)
            setSelectedCandidate(null)
            // Refetch candidates list
            await refetchCandidates()
        } catch (error) {
            console.error('Error assigning candidate:', error)
            alert(t`Failed to assign candidate. Please try again.`)
        } finally {
            setIsAssigningCandidate(false)
        }
    }

    const handleCloseAddCandidateModal = () => {
        setIsAddCandidateModalOpen(false)
        setSelectedCandidate(null)
    }

    if (!carePartner) {
        return (
            <div className='flex flex-col justify-center items-center h-full'>
                <LoadingComponent />
            </div>
        )
    }

    return (
        <>
            <GenericDetailPage
                backButtonText={t`To Care Partners`}
                title={
                    <div className='flex items-center gap-3'>
                        <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0'>
                            <HeartHandshake size={20} />
                        </div>
                        <div className='min-w-0'>
                            <Typography variant='h6' className='font-semibold truncate'>
                                {`${carePartner.firstName} ${carePartner.lastName}`}
                            </Typography>
                            <div className='flex items-center gap-2 mt-1'>
                                <CopyableIdentifier identifier={carePartner.friendlyId} />
                                <span className='text-xs text-gray-500'>ID: {carePartner.id}</span>
                            </div>
                        </div>
                    </div>
                }
                data={carePartner}
                columns={[]}
                backRoute='/carepartners'
                actionButtons={[]}
                additionalComponents={[
                    <div key='content-layout' className='grid grid-cols-1 md:grid-cols-12 gap-4 pb-16 min-h-0'>
                        <div className='md:col-span-8 space-y-4 min-h-0'>
                            <CarePartnerInfoSection
                                title='Contact Information'
                                icon={<HeartHandshake size={18} />}
                                titleAction={
                                    <div className='flex gap-2'>
                                        {/* Only show "Add Additional Phone" button if there's at least one slot available */}
                                        {(!carePartner.additionalPhone1 || !carePartner.additionalPhone2) && (
                                            <Button
                                                color='primary'
                                                onClick={() => {
                                                    setEditingPhoneSlot(undefined)
                                                    setIsAdditionalPhoneModalOpen(true)
                                                }}
                                                size='small'
                                                variant='outlined'
                                                className='h-8'
                                                startIcon={<Phone size={16} />}
                                            >
                                                {t`Add Additional Phone`}
                                            </Button>
                                        )}
                                        <Button
                                            color='primary'
                                            onClick={() => setIsEditContactModalOpen(true)}
                                            size='small'
                                            variant='outlined'
                                            className='h-8'
                                            startIcon={<Edit size={16} />}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                }
                            >
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Email`} value={carePartner.email} />
                                    <InfoField label={t`Phone`} value={carePartner.phoneNumber} />
                                    {(carePartner.additionalPhone1 || carePartner.additionalPhone2) && (
                                        <>
                                            {carePartner.additionalPhone1 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {carePartner.additionalPhone1Label
                                                                ? `${carePartner.additionalPhone1Label} Phone`
                                                                : t`Additional Phone 1`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone1')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{carePartner.additionalPhone1 || 'N/A'}</p>
                                                </div>
                                            )}
                                            {carePartner.additionalPhone2 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {carePartner.additionalPhone2Label
                                                                ? `${carePartner.additionalPhone2Label} Phone`
                                                                : t`Additional Phone 2`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone2')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{carePartner.additionalPhone2 || 'N/A'}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <InfoField label={t`Date of Birth`} value={formatDate(carePartner.dateOfBirth)} />
                                    <InfoField
                                        label={t`Preferred Contact Time`}
                                        value={formatTimeSlot(carePartner.preferredContactStartDatetime, carePartner.preferredContactEndDatetime)}
                                        status={carePartner.status}
                                    />
                                    <InfoField label={t`SMS Consent`} value={carePartner.smsConsent ? 'Yes' : 'No'} />
                                    <InfoField label={t`Language Preference`} value={carePartner.languagePreference || 'N/A'} />
                                </div>
                            </CarePartnerInfoSection>

                            <CarePartnerInfoSection title='Study Information' icon={<Calendar size={18} />}>
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Study Name`} value={user?.org_name} />
                                    <InfoField label={t`Created At`} value={formatDate(carePartner.createdDttm)} />
                                    <InfoField label={t`Updated At`} value={formatDate(carePartner.updatedDttm)} />
                                </div>
                            </CarePartnerInfoSection>

                            <CarePartnerInfoSection
                                title='Contact Attempts'
                                icon={<PhoneIcon className='text-primary-main h-5 w-5' />}
                                titleAction={
                                    <Button
                                        color='primary'
                                        onClick={handleOpenModal}
                                        startIcon={<span>{t`+`}</span>}
                                        className='bg-primary-main text-white hover:bg-gray-200 rounded-md h-8 mb-4'
                                        size='small'
                                    >
                                        {t`Add New Attempt`}
                                    </Button>
                                }
                            >
                                {contactAttemptsLoading ? (
                                    <LoadingComponent />
                                ) : (
                                    <div className='h-[200px] overflow-visible'>
                                        <LogAttemptTable attempts={contactAttempts} onEdit={handleEditAttempt} />
                                    </div>
                                )}
                            </CarePartnerInfoSection>

                            <SubjectHistory
                                subjectType='care-partner'
                                subjectId={id || ''}
                                onUpdate={fetchCarePartner}
                                patientManagers={patientManagers}
                            />
                        </div>

                        <div className='md:col-span-4 min-h-0'>
                            <CarePartnerInfoSection title='Additional Details' icon={<HeartHandshake size={18} />}>
                                <div className='space-y-3'>
                                    <InfoField label={t`First Name`} value={carePartner.firstName} />
                                    <InfoField label={t`Last Name`} value={carePartner.lastName} />
                                    <div className='mb-3 last:mb-0'>
                                        <p className='text-xs text-gray-500 mb-0.5'>{t`Status`}</p>
                                        {carePartner.status ? (
                                            <StatusBadge status={carePartner.status} />
                                        ) : (
                                            <span className='text-sm font-medium text-gray-400'>-</span>
                                        )}
                                    </div>
                                </div>
                            </CarePartnerInfoSection>

                            {/* Assigned Candidates Section */}
                            <CarePartnerInfoSection
                                title={`Assigned Candidates (${candidatesResponse?.data?.length || 0})`}
                                icon={<Users size={18} />}
                                titleAction={
                                    <Button
                                        color='primary'
                                        onClick={handleAddCandidateClick}
                                        size='small'
                                        variant='outlined'
                                        className='h-8'
                                        startIcon={<Plus size={16} />}
                                    >
                                        {t`Add Candidate`}
                                    </Button>
                                }
                            >
                                {candidatesLoading ? (
                                    <LoadingComponent />
                                ) : candidatesResponse?.data && candidatesResponse.data.length > 0 ? (
                                    <div className='space-y-2 max-h-[300px] overflow-y-auto'>
                                        {candidatesResponse.data.map((candidate) => (
                                            <div
                                                key={candidate.id}
                                                className='p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors relative group'
                                            >
                                                <div
                                                    className='flex items-center justify-between cursor-pointer'
                                                    onClick={() => history.push(`/candidates/${candidate.id}`)}
                                                >
                                                    <div className='flex-1 min-w-0'>
                                                        <p className='text-sm font-medium truncate'>
                                                            {candidate.firstName} {candidate.lastName}
                                                        </p>
                                                        <p className='text-xs text-gray-500'>{formatDate(candidate.dateOfBirth)}</p>
                                                        {candidate.phone && <p className='text-xs text-gray-500'>{candidate.phone}</p>}
                                                    </div>
                                                    <div className='ml-2 flex items-center gap-2 flex-shrink-0'>
                                                        {candidate.status && (
                                                            <div className='flex items-center justify-center'>
                                                                <StatusBadge status={candidate.status} />
                                                            </div>
                                                        )}
                                                        <IconButton
                                                            size='small'
                                                            onClick={(e) => handleRemoveCandidate(candidate.id, e)}
                                                            disabled={isRemovingCandidate === candidate.id}
                                                            className='opacity-0 group-hover:opacity-100 transition-opacity'
                                                            sx={{
                                                                color: 'error.main',
                                                                '&:hover': { backgroundColor: 'error.light', color: 'error.dark' }
                                                            }}
                                                        >
                                                            {isRemovingCandidate === candidate.id ? <LoadingComponent /> : <X size={16} />}
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className='text-center py-4 text-gray-500 text-sm'>
                                        {t`No candidates assigned. Click "Add Candidate" to assign one.`}
                                    </div>
                                )}
                            </CarePartnerInfoSection>

                            {/* Assigned Patients Section - Only show if there are patients */}
                            {!patientsLoading && patientsResponse?.data && patientsResponse.data.length > 0 && (
                                <CarePartnerInfoSection title={`Assigned Patients (${patientsResponse.data.length})`} icon={<Users size={18} />}>
                                    <div className='space-y-2 max-h-[300px] overflow-y-auto'>
                                        {patientsResponse.data.map((patient) => (
                                            <div
                                                key={patient.id}
                                                className='p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors'
                                                onClick={() => history.push(`/patients/${patient.id}`)}
                                            >
                                                <div className='flex items-center justify-between'>
                                                    <div className='flex-1 min-w-0'>
                                                        <p className='text-sm font-medium truncate'>
                                                            {patient.firstName} {patient.lastName}
                                                        </p>
                                                        <p className='text-xs text-gray-500'>{formatDate(patient.dateOfBirth)}</p>
                                                        {patient.mobilePhone && <p className='text-xs text-gray-500'>{patient.mobilePhone}</p>}
                                                    </div>
                                                    {patient.greenlightId && (
                                                        <div className='ml-2 flex-shrink-0'>
                                                            <span className='text-xs text-green-600 font-medium'>{t`Enrolled`}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CarePartnerInfoSection>
                            )}
                        </div>
                    </div>
                ]}
            />

            {/* Modal for adding/editing contact attempt */}
            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        width: '500px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <div className='flex justify-center items-center min-h-[50vh]'>
                        <div className='w-full'>
                            <LogAttemptInput
                                entityName={`${carePartner.firstName} ${carePartner.lastName}`}
                                onSubmit={editingAttempt ? handleUpdateAttempt : handleSubmitAttempt}
                                onCancel={handleCloseModal}
                                entityType='care-partner'
                                entityId={carePartner.id}
                                contactAttempts={contactAttempts}
                                isLoading={contactAttemptsLoading}
                                error={contactAttemptsError}
                                initialValues={
                                    editingAttempt
                                        ? {
                                              contactType: editingAttempt.contactType,
                                              outcome: editingAttempt.attemptOutcome,
                                              notes: editingAttempt.notes,
                                              nextContactTime: editingAttempt.suggestedNextContactTime
                                                  ? new Date(editingAttempt.suggestedNextContactTime)
                                                  : null
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal for editing contact information */}
            <EditContactInfoModal
                open={isEditContactModalOpen}
                onClose={() => setIsEditContactModalOpen(false)}
                onSave={handleUpdateContactInfo}
                carePartner={carePartner}
                loading={isUpdatingContact}
            />

            <AdditionalPhoneModal
                open={isAdditionalPhoneModalOpen}
                onClose={() => {
                    setIsAdditionalPhoneModalOpen(false)
                    setEditingPhoneSlot(undefined)
                }}
                onSave={handleUpdateAdditionalPhones}
                initialData={{
                    phone1: carePartner?.additionalPhone1 || null,
                    phone1Label: carePartner?.additionalPhone1Label || null,
                    phone2: carePartner?.additionalPhone2 || null,
                    phone2Label: carePartner?.additionalPhone2Label || null
                }}
                loading={isUpdatingAdditionalPhones}
                entityType='care-partner'
                editingSlot={editingPhoneSlot}
            />

            {/* Modal for adding/assigning a candidate */}
            <Dialog
                open={isAddCandidateModalOpen}
                onClose={handleCloseAddCandidateModal}
                maxWidth='sm'
                fullWidth
                PaperProps={{
                    sx: {
                        width: '500px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle>{t`Assign Candidate to Care Partner`}</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <div className='space-y-4'>
                        <CandidateSearchDropdown
                            value={selectedCandidate}
                            onChange={setSelectedCandidate}
                            label={t`Select Candidate`}
                            placeholder={t`Search candidates by name, DOB, or phone...`}
                            disabled={isAssigningCandidate}
                        />
                        {selectedCandidate && (
                            <div className='mt-4 p-3 bg-gray-50 rounded-md'>
                                <Typography variant='body2' className='font-medium mb-1'>
                                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                                </Typography>
                                {selectedCandidate.dateOfBirth && (
                                    <Typography variant='caption' className='text-gray-600 block'>
                                        {t`DOB:`} {formatDate(selectedCandidate.dateOfBirth)}
                                    </Typography>
                                )}
                                {selectedCandidate.phone && (
                                    <Typography variant='caption' className='text-gray-600 block'>
                                        {t`Phone:`} {selectedCandidate.phone}
                                    </Typography>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={handleCloseAddCandidateModal} disabled={isAssigningCandidate}>
                        {t`Cancel`}
                    </Button>
                    <Button onClick={handleAssignCandidate} variant='contained' color='primary' disabled={!selectedCandidate || isAssigningCandidate}>
                        {isAssigningCandidate ? t`Assigning...` : t`Assign Candidate`}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default CarePartnerDetailsPage
